/**
 * A plain durable job read as the stages it reported, from its event log.
 *
 * Workflow jobs write checkpoints, and `./workflow-steps` reads those. A plain
 * job such as `consumer.studio_render` writes none: all it leaves is its row
 * in `fastapi_worker_job_events`, which the job-detail read returns as
 * `events` (newest 200, oldest first). Its `progress` events carry a `code`
 * naming the stage it has reached and, usually, how far along it is
 * (`progress_current` of `progress_total`).
 *
 * - A step is a run of progress events with the same code in the same attempt.
 *   It starts at its first event and ends when the next step starts, or when
 *   the attempt ends (`succeeded`, `retry_scheduled`, `dead_lettered`,
 *   `lease_expired`, `cancelled`).
 * - The step an attempt ended in takes that ending: completed, failed (with
 *   the event's code and reference), interrupted or cancelled.
 * - Everything else - queued, dispatched, claimed, lease renewals - is
 *   bookkeeping and goes in a collapsed technical timeline, with runs of lease
 *   renewals folded into one line.
 *
 * Only used when a job has no checkpoints; a checkpointed job reads as before.
 */

import { useState } from 'react';
import type { JobEvent } from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, StatusBadge } from '@ui/console/primitives';
import { formatDuration } from '@ui/console/measures';
import { formatTimestamp, humanize } from '@ui/console/format';
import { cx } from '@ui/utils/cx';
import { CopyableId } from './references';
import {
  LogCapNote,
  LogLine,
  TimelineLabel,
  type StepLogEntry,
  type StepStatus,
} from './workflow-steps';

type Row = Record<string, unknown>;

function record(value: unknown): Row | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : undefined;
}

function num(row: Row, key: string): number | null {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(row: Row, key: string): string | null {
  const value = row[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function time(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** `MAX_JOB_EVENTS` upstream: the read returns only the newest events. */
export const MAX_JOB_EVENTS = 200;

/** A full page of events may be missing older ones; a shorter one is the whole history. */
export function eventsCapped(events: readonly unknown[] | undefined): boolean {
  return (events?.length ?? 0) >= MAX_JOB_EVENTS;
}

/** Said wherever the job's events are read while they are at the cap. */
export function EventCapNote() {
  return (
    <span className="text-xs text-quaternary" data-part="event-cap">
      Showing the newest {MAX_JOB_EVENTS} events; earlier history is not shown.
    </span>
  );
}

/**
 * The job's events, oldest first, or `undefined` when the read carries no
 * `events` field at all (an API from before it existed).
 */
export function eventsOf(job: Row | undefined): JobEvent[] | undefined {
  const raw = job?.['events'];
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((entry) => record(entry))
    .filter((entry): entry is Row => Boolean(entry))
    .map((entry) => ({
      sequence: num(entry, 'sequence') ?? 0,
      event_type: str(entry, 'event_type') ?? 'unknown',
      attempt: num(entry, 'attempt'),
      state_version: num(entry, 'state_version') ?? 0,
      progress_current: num(entry, 'progress_current'),
      progress_total: num(entry, 'progress_total'),
      code: str(entry, 'code'),
      reference_id: str(entry, 'reference_id'),
      created_at: str(entry, 'created_at') ?? '',
    }))
    .sort((left, right) => left.sequence - right.sequence);
}

/* ---------------------------------------------------------------- reading */

export type EventStepStatus = StepStatus | 'cancelled';

export type EventStep = {
  key: string;
  /** The progress code as the job wrote it; `null` when it wrote none. */
  code: string | null;
  label: string;
  attempt: number;
  /** Where this stage sits in the job's own count, when it reported one. */
  position: { current: number; total: number | null } | undefined;
  status: EventStepStatus;
  startedAt: string | undefined;
  finishedAt: string | undefined;
  failure: { code: string | null; reference: string | null } | undefined;
  sequence: number;
};

const ATTEMPT_ENDINGS: Record<string, EventStepStatus> = {
  succeeded: 'completed',
  retry_scheduled: 'failed',
  dead_lettered: 'failed',
  lease_expired: 'interrupted',
  cancelled: 'cancelled',
};

/** A progress code in plain words: `authorizing` reads "Authorizing". */
export function stageLabel(code: string | null): string {
  return code ? humanize(code) : 'Working';
}

export function readEventSteps({
  events,
  jobStatus,
  lastErrorCode,
}: {
  events: readonly JobEvent[];
  jobStatus: string | undefined;
  lastErrorCode: string | undefined;
}): EventStep[] {
  const ordered = [...events].sort((left, right) => left.sequence - right.sequence);
  const steps: EventStep[] = [];
  let open: EventStep | undefined;
  let attempt = 1;

  const close = (
    status: EventStepStatus,
    at: string | undefined,
    failure?: EventStep['failure'],
  ) => {
    if (!open) return;
    open.status = status;
    open.finishedAt = at;
    if (failure) open.failure = failure;
    open = undefined;
  };

  for (const event of ordered) {
    const at = event.created_at || undefined;
    if (event.event_type === 'claimed') {
      // A new attempt: anything still open was left behind by the last one.
      close('interrupted', undefined);
      attempt = event.attempt ?? attempt + 1;
      continue;
    }
    const ending = ATTEMPT_ENDINGS[event.event_type];
    if (ending) {
      close(
        ending,
        at,
        ending === 'completed' ? undefined : { code: event.code, reference: event.reference_id },
      );
      continue;
    }
    if (event.event_type !== 'progress') continue;

    const stepAttempt = event.attempt ?? attempt;
    attempt = stepAttempt;
    const sameStep =
      open && open.attempt === stepAttempt && (event.code === null || event.code === open.code);
    if (sameStep && open) {
      open.sequence = event.sequence;
      continue;
    }
    close('completed', at);
    const code = event.code;
    open = {
      key: `${code ?? 'working'}#${stepAttempt}#${event.sequence}`,
      code,
      label: stageLabel(code),
      attempt: stepAttempt,
      position:
        event.progress_current !== null
          ? { current: event.progress_current, total: event.progress_total }
          : undefined,
      status: 'running',
      startedAt: at,
      finishedAt: undefined,
      failure: undefined,
      sequence: event.sequence,
    };
    steps.push(open);
  }

  // The events stop without an ending: the job row says where it got to.
  if (open) {
    const last: EventStep = open;
    if (jobStatus === 'running' && last.attempt === attempt) last.status = 'running';
    else if (jobStatus === 'succeeded') close('completed', undefined);
    else if (jobStatus === 'dead_lettered')
      close('failed', undefined, { code: lastErrorCode ?? null, reference: null });
    else if (jobStatus === 'cancelled') close('cancelled', undefined);
    else close('interrupted', undefined);
  }

  return steps;
}

/* -------------------------------------------------------------- rendering */

/** What a failure code means, where it is known; its words otherwise. */
const FAILURE_WORDS: Record<string, string> = {
  provider_rejected: 'The image provider refused the request.',
  provider_timeout: 'The provider took too long to answer.',
  provider_unavailable: 'The provider could not be reached.',
  lease_expired: 'The worker stopped responding before it finished.',
  arq_dispatch_failed: 'The job could not be handed to a worker.',
  operator_cancelled: 'Someone on the team stopped it.',
  cancel_requested: 'Someone asked for it to stop.',
};

export function failureWords(code: string | null): string {
  if (!code) return 'No reason was recorded.';
  return FAILURE_WORDS[code] ?? `${humanize(code)}.`;
}

function when(value: string | undefined): string {
  return value ? formatTimestamp(value) : 'Not recorded';
}

function durationOf(step: EventStep): string | undefined {
  const start = time(step.startedAt);
  const end = time(step.finishedAt);
  if (start === undefined || end === undefined || end < start) return undefined;
  return formatDuration((end - start) / 1000);
}

function EventStepCard({ step, showAttempt }: { step: EventStep; showAttempt: boolean }) {
  const duration = durationOf(step);
  const failed = step.status === 'failed';
  const stopped = failed || step.status === 'interrupted' || step.status === 'cancelled';
  return (
    <li
      data-step={step.code ?? ''}
      data-step-attempt={step.attempt}
      data-step-status={step.status}
      className={cx(
        'flex flex-col gap-3 rounded-xl border p-4',
        failed ? 'border-error-secondary bg-error-primary' : 'border-secondary bg-primary',
      )}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1" data-part="header">
        <h3 className="text-sm font-semibold text-primary">{step.label}</h3>
        <StatusBadge value={step.status} />
        {step.position ? (
          <span className="text-xs text-tertiary" data-part="position">
            {step.position.total !== null
              ? `${step.position.current} of ${step.position.total}`
              : `Step ${step.position.current}`}
          </span>
        ) : null}
        {showAttempt ? <span className="text-xs text-tertiary">Attempt {step.attempt}</span> : null}
        <span className="text-xs text-tertiary">Started {when(step.startedAt)}</span>
        <span className="text-xs text-tertiary">
          {duration
            ? `Took ${duration}`
            : step.status === 'running'
              ? 'Still running'
              : 'Duration not known'}
        </span>
      </header>
      {stopped && step.failure ? (
        <Callout
          tone={failed ? 'error' : 'warning'}
          title={failed ? `Stopped here: ${failureWords(step.failure.code)}` : 'Stopped here.'}
        >
          <span className="flex flex-col items-start gap-1.5">
            {!failed && step.failure.code ? <span>{failureWords(step.failure.code)}</span> : null}
            {step.failure.code ? <Code>{step.failure.code}</Code> : null}
            {step.failure.reference ? (
              <span className="flex flex-wrap items-center gap-1.5 text-xs opacity-75">
                Reference
                <CopyableId value={step.failure.reference} label="reference" full />
              </span>
            ) : null}
          </span>
        </Callout>
      ) : null}
    </li>
  );
}

export function EventSteps({ steps }: { steps: readonly EventStep[] }) {
  const showAttempt = new Set(steps.map((step) => step.attempt)).size > 1;
  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0" data-part="event-steps">
      {steps.map((step) => (
        <EventStepCard key={step.key} step={step} showAttempt={showAttempt} />
      ))}
    </ol>
  );
}

const EVENT_WORDS: Record<string, (event: JobEvent) => string> = {
  enqueued: () => 'Accepted into the queue',
  dispatch_failed: () => 'Could not be handed to a worker',
  dispatched: () => 'Handed to a worker',
  claimed: (event) => `Attempt ${event.attempt ?? '?'} picked up by a worker`,
  lease_renewed: () => 'Worker confirmed it was still working',
  lease_expired: () => 'Worker stopped responding',
  retry_scheduled: (event) => `Attempt ${event.attempt ?? '?'} failed; another try was scheduled`,
  succeeded: () => 'Finished',
  dead_lettered: () => 'Stopped after using every attempt',
  cancel_requested: () => 'Someone asked for it to stop',
  cancelled: () => 'Stopped',
  redriven: () => 'Put back in the queue by an operator',
  resumed: () => 'Resumed by an operator',
};

type TimelineEntry = { first: JobEvent; last: JobEvent; count: number };

/** Lifecycle events only, with each run of lease renewals folded into one line. */
export function lifecycleEntries(events: readonly JobEvent[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  for (const event of events) {
    if (event.event_type === 'progress') continue;
    const previous = entries[entries.length - 1];
    if (event.event_type === 'lease_renewed' && previous?.first.event_type === 'lease_renewed') {
      previous.last = event;
      previous.count += 1;
      continue;
    }
    entries.push({ first: event, last: event, count: 1 });
  }
  return entries;
}

export function EventTimeline({
  events,
  logs,
  logsCapped = false,
}: {
  events: readonly JobEvent[];
  logs: readonly StepLogEntry[];
  /** The run log is at its cap, so older lines were dropped. */
  logsCapped?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const entries = lifecycleEntries([...events].sort((l, r) => l.sequence - r.sequence));
  return (
    <div className="flex flex-col gap-3" data-part="technical-timeline">
      <div>
        <Button
          size="sm"
          color="secondary"
          aria-expanded={open}
          aria-controls="workflow-technical-timeline"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Hide technical timeline' : 'Show technical timeline'}
        </Button>
      </div>
      {open ? (
        <div id="workflow-technical-timeline" className="flex flex-col gap-4">
          {eventsCapped(events) ? <EventCapNote /> : null}
          {entries.length ? (
            <ol className="m-0 flex list-none flex-col gap-1 border-l-2 border-secondary p-0 pl-4">
              {entries.map(({ first, last, count }) => {
                const words = EVENT_WORDS[first.event_type];
                const base = words ? words(first) : humanize(first.event_type);
                const label = count > 1 ? `${base} (${count} times)` : base;
                const raw =
                  count > 1
                    ? `${first.event_type} #${first.sequence}–#${last.sequence}`
                    : `${first.event_type} #${first.sequence}`;
                return (
                  <li
                    key={first.sequence}
                    className="flex flex-col gap-0.5 py-1"
                    data-timeline-entry="event"
                    data-event-type={first.event_type}
                  >
                    <span className="flex flex-wrap items-center gap-x-3">
                      <TimelineLabel label={label} raw={raw} />
                      {first.attempt !== null ? (
                        <span className="text-xs text-tertiary">attempt {first.attempt}</span>
                      ) : null}
                      <span className="text-xs text-tertiary">
                        {count > 1
                          ? `${when(first.created_at)} – ${when(last.created_at)}`
                          : when(first.created_at)}
                      </span>
                      {first.code ? <Code>{first.code}</Code> : null}
                      {first.reference_id ? (
                        <CopyableId value={first.reference_id} label="reference" />
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ol>
          ) : (
            <span className="text-sm text-quaternary">
              {eventsCapped(events)
                ? 'The newest events are all progress; nothing else is among them.'
                : 'This job recorded nothing beyond its progress.'}
            </span>
          )}
          {logs.length ? (
            <div className="flex flex-col gap-1">
              <h4 className="text-xs font-semibold tracking-wide text-tertiary uppercase">
                Log lines
              </h4>
              {logsCapped ? <LogCapNote /> : null}
              <ul className="m-0 flex list-none flex-col divide-y divide-secondary p-0">
                {logs.map((entry, index) => (
                  <LogLine key={`${entry.timestamp ?? index}-${index}`} entry={entry} />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
